import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Field, YesObject } from '@yes.technology/react-toolkit'
import getRelationCounterpartField from 'site/Renderer/components/ObjectView/lib/getRelationCountertpartField/getRelationCounterpartField'
import fetchYesObjects from '../api/fetchYesObjects'

export async function populateObjectRelations(
  objects: YesObject[],
  relationFields: Field[],
  onProgress?: (count: number) => void
): Promise<YesObject[]> {
  const objectsMap = new Map(objects.map((obj) => [obj.uuid, { ...obj }]))
  let requestCount = 0

  await Promise.all(
    relationFields.map(async (relationField) => {
      const objectsWithRelation = objects.filter(
        (obj) => obj?.[relationField.column_json]
      )

      if (objectsWithRelation.length === 0) return

      const firstObject = objectsWithRelation[0]
      const relationCounterpartField = getRelationCounterpartField({
        relationFieldRelation: relationField.relation,
        fieldValues: firstObject
      })

      const [relationCounterpartColumn] = Object.keys(
        relationCounterpartField || {}
      )

      if (!relationCounterpartColumn) return

      const uuidsToFetch = objectsWithRelation.map((obj) => obj.uuid)

      const chunkSize = 100
      const chunks = []
      for (let i = 0; i < uuidsToFetch.length; i += chunkSize) {
        chunks.push(uuidsToFetch.slice(i, i + chunkSize))
      }

      await Promise.all(
        chunks.map(async (chunkUUIDs) => {
          const response = await fetchYesObjects({
            filterBody: {
              query: {
                $and: [
                  {
                    [`${relationCounterpartColumn}.uuid`]: {
                      $in: chunkUUIDs
                    },
                    'situation.des': {
                      $eq: '40'
                    }
                  }
                ]
              },
              objectclass: relationField.relation?.uuid_objectclass || '',
              orderby: [],
              pagination: {}
            }
          })

          requestCount++
          onProgress?.(requestCount)

          if (response?.objects?.length) {
            const chunkResults = new Map<string, YesObject[]>()

            response.objects.forEach((fetchedObj) => {
              const parentRef = fetchedObj[relationCounterpartColumn]
              const parentRefs = Array.isArray(parentRef)
                ? parentRef
                : parentRef
                  ? [parentRef]
                  : []

              parentRefs.forEach((ref: any) => {
                if (ref?.uuid) {
                  if (!chunkResults.has(ref.uuid)) {
                    chunkResults.set(ref.uuid, [])
                  }
                  chunkResults.get(ref.uuid)!.push(fetchedObj)
                }
              })
            })

            chunkResults.forEach((children, parentUUID) => {
              if (objectsMap.has(parentUUID)) {
                const parentObj = objectsMap.get(parentUUID)!
                parentObj[relationField.column_json] = children
              }
            })
          }
        })
      )
    })
  )

  return Array.from(objectsMap.values())
}

export default function usePopulateObjectRelations({
  objects,
  relationFields,
  enabled
}: {
  objects: YesObject[]
  relationFields: Field[]
  enabled?: boolean
}) {
  const [batchProgress, setBatchProgress] = useState({
    currentBatch: 0,
    isFetching: false
  })

  const { data = [], ...queryInfo } = useQuery<YesObject[], Error>({
    queryKey: [
      'all-objects',
      objects.map((object) => object.uuid),
      relationFields.map((relationField) => relationField.uuid)
    ],
    queryFn: async () => {
      setBatchProgress({
        currentBatch: 1,
        isFetching: true
      })
      try {
        return await populateObjectRelations(
          objects,
          relationFields,
          (count) => {
            setBatchProgress((prev) => ({
              ...prev,
              currentBatch: count
            }))
          }
        )
      } finally {
        setBatchProgress((prev) => ({
          ...prev,
          isFetching: false
        }))
      }
    },
    enabled: enabled && objects.length > 0,
    staleTime: 0,
    gcTime: 0
  })

  return {
    data,
    populateObjectRelationsProgress: batchProgress,
    ...queryInfo
  }
}
